import { ApiResponse } from './api.types';

export enum QuestionAnswerType {
  ShortAnswer = 1,
  LongAnswer = 2,
  RadioButton = 3,
  Checkbox = 4,
  YesNo = 5,
  Date = 6,
  Dropdown = 7,
  NumericInteger = 8,
  NumericDecimal = 9,
  Currency = 10,
  Table = 11,
  Scale = 12,
  Photo = 13,
  Signature = 14,
  File = 15,
  Rating = 16,
  Location = 17,
}

export interface DisplayRule {
  targetQuestionOrder: number;
  condition: 'equals' | 'not_equals' | 'notEquals' | 'contains' | 'not_contains' | 'notContains' | 'greater_than' | 'greaterThan' | 'less_than' | 'lessThan' | 'greaterOrEqual' | 'greater_or_equal' | 'lessOrEqual' | 'less_or_equal' | 'isEmpty' | 'isNotEmpty';
  value: string;
  action: 'show' | 'hide';
}

export interface TableColumn {
  name: string;
  order: number;
}

export interface Question {
  id: string;
  title: string;
  answerType: string;
  order: number;
  isRequired: boolean;
  showInSimplifiedReport: boolean;
  hasHint: boolean;
  hintText?: string;
  hasDisplayRule: boolean;
  displayRule?: DisplayRule;

  options?: string[];
  maxCharacters?: number;
  decimalPlaces?: number;
  tableColumns?: TableColumn[];
  description?: string;
  maxPhotos?: number;
  allowGallerySelection?: boolean;
  maxFiles?: number;
  allowedFileTypes?: ('Image' | 'PDF')[];
  answerKey?: string;
  tags?: string[];
  tagIds?: string[];
}

export interface Section {
  id: string;
  name: string;
  order: number;
  questions: Question[];
}

export interface Group {
  id: string;
  name: string;
  order: number;
  sections: Section[];
}

export interface Checklist {
  id: string;
  name: string;
  inspectionTypeId: string;
  inspectionTypeName?: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  createdBy?: string;
  deactivatedAt?: string;
  deactivatedBy?: string;
  deletedBy?: string;
  groups: Group[];
}

export interface ChecklistVersion {
  id: string;
  checklistId: string;
  version: number;
  checklistName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateChecklistRequest {
  checklistName: string;
  inspectionTypeId: string;
  groups: CreateGroupRequest[];
}

export interface CreateGroupRequest {
  name: string;
  sections: CreateSectionRequest[];
}

export interface CreateSectionRequest {
  name: string;
  questions: CreateQuestionRequest[];
}

export interface CreateQuestionRequest {
  title: string;
  answerType: string;
  order?: number;
  isRequired: boolean;
  showInSimplifiedReport: boolean;
  hasHint: boolean;
  hintText?: string;
  hasDisplayRule: boolean;
  displayRule?: DisplayRule;
  options?: string[];
  maxCharacters?: number;
  decimalPlaces?: number;
  tableColumns?: TableColumn[];
  description?: string;
  maxPhotos?: number;
  allowGallerySelection?: boolean;
  maxFiles?: number;
  allowedFileTypes?: ('Image' | 'PDF')[];
  answerKey?: string;
  tags?: string[];
}

export interface UpdateChecklistRequest {
  checklistName: string;
  groups: CreateGroupRequest[];
}

export interface AddGroupRequest {
  name: string;
  order: number;
}

export interface UpdateGroupRequest {
  name: string;
  order: number;
}

export interface AddSectionRequest {
  name: string;
  order: number;
}

export interface UpdateSectionRequest {
  name: string;
  order: number;
}

export interface AddQuestionRequest {
  title: string;
  type: number;
  order: number;
  isRequired: boolean;
  showInSimplifiedReport: boolean;
  hasHint: boolean;
  hintText?: string;
  hasDisplayRule: boolean;
  displayRule?: DisplayRule;
  maxCharacters?: number;
  decimalPlaces?: number;
  tableColumns?: TableColumn[];
  description?: string;
  maxPhotos?: number;
  allowGallerySelection?: boolean;
  maxFiles?: number;
  allowedFileTypes?: ('Image' | 'PDF')[];
  answerKey?: string;
  tags?: string[];
}

export interface UpdateQuestionRequest extends AddQuestionRequest {}

export interface PhotoAnswer {
  fileName: string;
  fileUrl: string;
  bucketPath?: string;
  bucketName?: string;
  caption?: string;
  uploadedAt: string;
  fileSizeBytes?: number;
  imageWidth?: number;
  imageHeight?: number;
  fileHash?: string;
}

export interface FileAnswer {
  fileName: string;
  fileUrl: string;
  bucketPath?: string;
  bucketName?: string;
  fileType: string;
  uploadedAt: string;
  fileSizeBytes?: number;
  fileHash?: string;
  description?: string;
}

export interface TableRowData {
  columns: Record<string, string>;
  rowNumber?: number;
  notes?: string;
}

export interface ChecklistAnswer {
  questionId: string;
  questionAnswerType?: QuestionAnswerType;
  textValue?: string;
  selectedOption?: string;
  selectedOptions?: string[];
  dateValue?: string;
  numericValue?: number | string;
  tableData?: TableRowData[];
  photos?: PhotoAnswer[];
  files?: FileAnswer[];
  isNotApplicable?: boolean;
  notes?: string;
  respondedAt?: string;

  answerValue?: string;
}

export interface SubmitChecklistResponseRequest {
  checklistId: string;
  inspectionId: string;
  inspectionItemId?: string;
  answers: ChecklistAnswer[];
  version?: number;
}

export interface ChecklistResponse {
  id: string;
  checklistId: string;
  inspectionId: string;
  submittedAt: string;
  submittedBy: string;
  answers: ChecklistAnswer[];
  version?: number;
}

export type ChecklistApiResponse = ApiResponse<Checklist>;
export type ChecklistsApiResponse = ApiResponse<Checklist[]>;
export type ChecklistVersionsApiResponse = ApiResponse<ChecklistVersion[]>;
export type GroupApiResponse = ApiResponse<Group>;
export type GroupsApiResponse = ApiResponse<Group[]>;
export type SectionApiResponse = ApiResponse<Section>;
export type SectionsApiResponse = ApiResponse<Section[]>;
export type QuestionApiResponse = ApiResponse<Question>;
export type QuestionsApiResponse = ApiResponse<Question[]>;
export type ChecklistResponseApiResponse = ApiResponse<ChecklistResponse>;
export type ChecklistResponsesApiResponse = ApiResponse<ChecklistResponse[]>;
